import { Helmet } from "react-helmet-async";

export type SeoHeadProps = {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  /** Schema.org FAQPage - array [domanda, risposta] */
  faq?: Array<{ question: string; answer: string }>;
  /** Schema.org BreadcrumbList - [{ name, url }] */
  breadcrumbs?: Array<{ name: string; url: string }>;
  noIndex?: boolean;
};

const SITE_URL = "https://www.qrgratis.it";
const SITE_NAME = "QRGratis";

export function SeoHead({
  title,
  description,
  canonical,
  ogImage,
  faq,
  breadcrumbs,
  noIndex,
}: SeoHeadProps) {
  const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : SITE_URL;
  const ogImageUrl = ogImage ? `${SITE_URL}${ogImage}` : `${SITE_URL}/og-default.png`;

  const faqSchema = faq?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map(({ question, answer }) => ({
          "@type": "Question",
          name: question,
          acceptedAnswer: { "@type": "Answer", text: answer },
        })),
      }
    : null;

  const breadcrumbSchema =
    breadcrumbs?.length &&
    breadcrumbs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((item, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: item.name,
            item: item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`,
          })),
        }
      : null;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={canonicalUrl} />
      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="it_IT" />
      <meta property="og:site_name" content={SITE_NAME} />
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      )}
    </Helmet>
  );
}
